import { Link, router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import { AppShell, PageHeader } from '../../../components/layout';
import { Card, Button } from '../../../components/ui';
import { cn } from '../../../lib/cn';

export default function ActivityShow({ student, activity }) {
  const { t } = useTranslation();
  const returnTo = activity.return_to || { href: '/student/dashboard', label: t('practice.backToDashboard') };
  const handleStartPractice = () => {
    router.post(`/student/activities/${activity.id}/start`);
  };

  const formatQuestionSummary = (summary) => {
    const parts = [];
    if (summary.short_answer) {
      parts.push(`${summary.short_answer} ${t('questions.shortAnswer')}`);
    }
    if (summary.multiple_choice) {
      parts.push(`${summary.multiple_choice} ${t('questions.mcq')}`);
    }
    if (summary.essay) {
      parts.push(`${summary.essay} ${t('questions.essay')}`);
    }
    return parts.join(', ');
  };

  const getStimulusTypeLabel = (type) => {
    const labels = {
      article: t('questions.stimulus'),
      video: t('questions.stimulus'),
      image: t('questions.stimulus'),
    };
    return labels[type] || t('practice.reading');
  };

  return (
    <AppShell student={student} maxWidth="narrow" scrollTitle={<><Link href={returnTo.href} className="hover:text-blue-600 dark:hover:text-blue-400">{returnTo.label}</Link>{'  ›  '}{activity.title}</>}>
      <PageHeader
        backLink={returnTo.href}
        backLabel={returnTo.label}
      />

      <div className="flex flex-col items-center">
        <Card
          gradient
          elevated
          className="w-full max-w-2xl text-center"
          padding="lg"
        >
          <div className="py-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {activity.title}
            </h1>
            {activity.subtitle && (
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                {activity.subtitle}
              </p>
            )}

            <div className="w-16 h-px bg-gray-300 dark:bg-gray-600 mx-auto mb-8" />

            <div className="space-y-3 text-gray-600 dark:text-gray-400 mb-8">
              <p>
                <span className="font-medium text-gray-900 dark:text-white">
                  {getStimulusTypeLabel(activity.stimulus_type)}
                </span>
                {' '}({activity.estimated_minutes} {t('common.min')})
              </p>
              <p>
                <span className="font-medium text-gray-900 dark:text-white">{t('practice.questions')}: </span>
                {formatQuestionSummary(activity.question_summary)}
              </p>
            </div>

            {activity.skills && activity.skills.length > 0 && (
              <div className="mb-10">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  {t('practice.skillsOverview')}:
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {activity.skills.map((skill, index) => (
                    <span
                      key={index}
                      className={cn(
                        'inline-flex items-center px-3 py-1.5',
                        'bg-gray-100 dark:bg-gray-700 rounded-full',
                        'text-sm font-medium text-gray-700 dark:text-gray-300'
                      )}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {activity.description && (
              <p className="text-gray-600 dark:text-gray-400 mb-10 max-w-md mx-auto">
                {activity.description}
              </p>
            )}

            <Button
              variant="primary"
              size="lg"
              onClick={handleStartPractice}
              disabled={activity.status === 'locked'}
              className="min-w-[200px]"
            >
              {activity.status === 'in_progress' ? t('common.continue') : t('practiceChoose.beginReading')}
              <svg
                className="ml-2 w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Button>

            {activity.status === 'locked' && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                {t('common.locked')}
              </p>
            )}
          </div>
        </Card>
      </div>
    </AppShell>
  );
}
